"use client";

import { useEffect } from "react";
import {
  getGoogleAdsConversionId,
  getGoogleAdsPurchaseConversionLabel,
} from "@/lib/analytics-config";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const ADS_PURCHASE_PARAM = "ads_purchase";
const ADS_PURCHASE_VALUE_PARAM = "ads_value";
const ADS_PURCHASE_CURRENCY_PARAM = "ads_currency";
const MAX_GTAG_ATTEMPTS = 20;
const GTAG_RETRY_INTERVAL_MS = 250;
const MAX_PURCHASE_ATTEMPTS = 20;
const PURCHASE_RETRY_INTERVAL_MS = 1500;
const CONVERSION_EVENT_TIMEOUT_MS = 5000;

type VerifiedPurchaseResponse = {
  ready?: boolean;
  transactionId?: string;
  value?: number;
  currency?: string;
};

type GoogleAdsPurchaseConversionProps = {
  conversionId?: string | null;
  conversionLabel?: string | null;
};

export default function GoogleAdsPurchaseConversion({
  conversionId: runtimeConversionId,
  conversionLabel: runtimeConversionLabel,
}: GoogleAdsPurchaseConversionProps = {}) {
  const conversionId =
    runtimeConversionId?.trim() || getGoogleAdsConversionId();
  const conversionLabel =
    runtimeConversionLabel?.trim() || getGoogleAdsPurchaseConversionLabel();

  useEffect(() => {
    if (
      process.env.NODE_ENV !== "production" ||
      !conversionId ||
      !conversionLabel
    ) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const orderNo = params.get(ADS_PURCHASE_PARAM)?.trim();

    if (!orderNo) {
      return;
    }

    const storageKey = `mirofish_ads_purchase_tracked:${orderNo}`;

    const clearPurchaseParams = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete(ADS_PURCHASE_PARAM);
      url.searchParams.delete(ADS_PURCHASE_VALUE_PARAM);
      url.searchParams.delete(ADS_PURCHASE_CURRENCY_PARAM);
      window.history.replaceState(
        window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`
      );
    };

    try {
      if (window.localStorage.getItem(storageKey)) {
        clearPurchaseParams();
        return;
      }
    } catch {
      // Storage can be unavailable in private modes; the conversion can still be sent.
    }

    let cancelled = false;
    const controller = new AbortController();
    const timeoutIds = new Set<number>();

    const wait = (delay: number) =>
      new Promise<void>((resolve) => {
        const timeoutId = window.setTimeout(() => {
          timeoutIds.delete(timeoutId);
          resolve();
        }, delay);
        timeoutIds.add(timeoutId);
      });

    const waitForGtag = async () => {
      for (let attempt = 0; attempt < MAX_GTAG_ATTEMPTS; attempt += 1) {
        if (cancelled) {
          return false;
        }
        if (window.gtag) {
          return true;
        }
        await wait(GTAG_RETRY_INTERVAL_MS);
      }

      return false;
    };

    const fetchVerifiedPurchase = async () => {
      for (let attempt = 0; attempt < MAX_PURCHASE_ATTEMPTS; attempt += 1) {
        if (cancelled) {
          return null;
        }

        const response = await fetch(
          `/api/analytics/google-ads/purchase?order_no=${encodeURIComponent(orderNo)}`,
          {
            credentials: "same-origin",
            cache: "no-store",
            signal: controller.signal,
          }
        );

        if (response.status === 202) {
          await wait(PURCHASE_RETRY_INTERVAL_MS);
          continue;
        }

        if (!response.ok) {
          clearPurchaseParams();
          return null;
        }

        const payload = (await response.json()) as VerifiedPurchaseResponse;
        const transactionId = payload.transactionId?.trim() || "";
        const value = payload.value;
        const currency = payload.currency?.trim().toUpperCase() || "";

        if (
          payload.ready !== true ||
          !transactionId ||
          typeof value !== "number" ||
          !Number.isFinite(value) ||
          value <= 0 ||
          !/^[A-Z]{3}$/.test(currency)
        ) {
          clearPurchaseParams();
          return null;
        }

        return { transactionId, value, currency };
      }

      return null;
    };

    const trackPurchase = async () => {
      try {
        const purchase = await fetchVerifiedPurchase();
        if (!purchase || cancelled || !(await waitForGtag()) || !window.gtag) {
          return;
        }

        let completed = false;
        const completeTracking = () => {
          if (completed) {
            return;
          }
          completed = true;

          try {
            window.localStorage.setItem(storageKey, "1");
          } catch {
            // Ignore storage failures after Google has processed the event command.
          }
          clearPurchaseParams();
          console.info("google_ads_purchase_event_completed", {
            currency: purchase.currency,
          });
        };

        window.gtag("event", "conversion", {
          send_to: `${conversionId}/${conversionLabel}`,
          value: purchase.value,
          currency: purchase.currency,
          transaction_id: purchase.transactionId,
          event_callback: completeTracking,
          event_timeout: CONVERSION_EVENT_TIMEOUT_MS,
        });
        console.info("google_ads_purchase_event_queued", {
          currency: purchase.currency,
        });
      } catch (error) {
        if (!cancelled && !(error instanceof DOMException && error.name === "AbortError")) {
          console.warn("Google Ads purchase verification failed", error);
        }
      }
    };

    void trackPurchase();

    return () => {
      cancelled = true;
      controller.abort();
      for (const timeoutId of timeoutIds) {
        window.clearTimeout(timeoutId);
      }
      timeoutIds.clear();
    };
  }, [conversionId, conversionLabel]);

  return null;
}
